"use client";

import React, { useState, useEffect, useMemo } from "react";
import Link from "next/link";
import { FaChevronDown } from "react-icons/fa6";
import { FaFilter, FaTimes } from "react-icons/fa";
import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import PriceRangeSlider from './PriceRangeSlider';
import LoadingSpinner from '../LoadingSpinner';
import { getVehiculos } from "@/lib/strapi-simple";

// Mapeo de logos de marcas
const BRAND_LOGOS = {
  'Farizon': '/logos/farizon.png',
  'Ford': '/logos/ford.png',
  'Forland': '/logos/forland.png',
  'Geely': '/logos/geely.png',
  'JAC Motors': '/logos/jac.png',
  'Kia': '/logos/kia.png',
  'Lincoln': '/logos/lincoln.png',
  'Mazda': '/logos/mazda.png',
  'Shineray': '/logos/shineray.png',
  'SWM': '/logos/swm.png'
};

const ITEMS_PER_PAGE = 12; // Paginación para mejorar rendimiento

export function VehiclesList({ 
  selectedBrandProp = null, 
  showHeader = true,
  title = "Móntate en tu sueño",
  subtitle = "Explora nuestra amplia gama de vehículos disponibles."
}) {
  const [selectedBrand, setSelectedBrand] = useState(selectedBrandProp || 'Todas');
  const [priceRange, setPriceRange] = useState([0, 100000]);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [allProducts, setAllProducts] = useState([]);
  const [allBrands, setAllBrands] = useState([]);

  // Procesar datos de vehículos desde Strapi
  useEffect(() => {
    const processVehiclesData = async () => {
      setIsLoading(true);
      try {
        let processedProducts = [];
        let brands = [];
        
        // Intentar cargar desde Strapi
        let strapiVehiculos = await getVehiculos();
        
        if (selectedBrandProp) {
          // Si hay una marca específica, filtrar por esa marca
          strapiVehiculos = strapiVehiculos.filter(v => 
            v.marca === selectedBrandProp || v.marcaSlug === selectedBrandProp.toLowerCase()
          );
        }
        
        if (strapiVehiculos && strapiVehiculos.length > 0) {
          // Usar datos de Strapi (ya vienen procesados)
          processedProducts = strapiVehiculos.map(vehiculo => ({
            id: vehiculo.id,
            name: `${vehiculo.marca} ${vehiculo.modelo}`,
            brand: vehiculo.marca,
            price: `$${vehiculo.precio?.toLocaleString() || '0'}`,
            priceValue: vehiculo.precio || 0,
            image: vehiculo.imagen,
            categoria: vehiculo.categoria,
            year: vehiculo.año,
            combustible: 'Gasolina',
            transmision: 'Automática',
            motor: '2.0L',
            potencia: '150 HP',
            slug: vehiculo.slug,
            brandSlug: vehiculo.marcaSlug
          }));
          
          brands = [...new Set(processedProducts.map(p => p.brand))];
        } else {
          // No hay datos de Strapi - no mostrar vehículos dummy
          processedProducts = [];
          brands = [];
        }
        
        const prices = processedProducts.map(p => p.priceValue);
        
        setAllProducts(processedProducts);
        setAllBrands(brands);
        if (prices.length > 0) {
          setPriceRange([Math.min(...prices), Math.max(...prices)]);
        }
        setIsLoading(false);
      } catch (error) {
        console.error('Error processing vehicles data:', error);
        setIsLoading(false);
      }
    };

    processVehiclesData();
  }, [selectedBrandProp]);

  // Actualizar la marca seleccionada cuando cambie la prop
  useEffect(() => {
    if (selectedBrandProp) {
      setSelectedBrand(selectedBrandProp);
    }
  }, [selectedBrandProp]);

  // Filtrado de productos optimizado con useMemo
  const filteredProducts = useMemo(() => {
    if (!allProducts.length) return [];
    
    return allProducts.filter(product => {
      const matchesBrand = selectedBrand === 'Todas' || product.brand === selectedBrand;
      const matchesPrice = product.priceValue >= priceRange[0] && product.priceValue <= priceRange[1];
      return matchesBrand && matchesPrice;
    });
  }, [selectedBrand, priceRange, allProducts]);

  // Productos paginados
  const paginatedProducts = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredProducts.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [filteredProducts, currentPage]);

  // Total de páginas
  const totalPages = Math.ceil(filteredProducts.length / ITEMS_PER_PAGE);

  // Reset página cuando cambien filtros
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedBrand, priceRange]);



  return (
    <section className={`px-4 pb-16 md:pb-24 lg:pb-28 bg-gray-50 ${showHeader ? 'pt-32 md:pt-36 lg:pt-40' : 'pt-8'}`}>
      <div className="max-w-7xl mx-auto w-full">
        {showHeader && (
          <div className="mb-12 md:mb-18 lg:mb-20">
            <div className="mx-auto max-w-lg text-center">
              <p className="font-semibold text-gray-600 uppercase tracking-wide text-sm">Vehículos</p>
              
              {/* Logo de la marca si está preseleccionada */}
              {selectedBrandProp && (
                <div className="w-20 h-20 mx-auto mt-4 mb-6 bg-white rounded-full flex items-center justify-center shadow-lg border border-gray-200 p-3">
                  {BRAND_LOGOS[selectedBrandProp] ? (
                    <img 
                      src={BRAND_LOGOS[selectedBrandProp]} 
                      alt={selectedBrandProp}
                      className="w-full h-full object-contain"
                      onError={(e) => {
                        console.error(`Error loading header logo: ${BRAND_LOGOS[selectedBrandProp]}`);
                        e.target.style.display = 'none';
                        e.target.nextElementSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <div className={`w-full h-full flex items-center justify-center text-2xl font-bold text-gray-600 ${BRAND_LOGOS[selectedBrandProp] ? 'hidden' : ''}`}>
                    {selectedBrandProp.charAt(0)}
                  </div>
                </div>
              )}
              
              <h1 className="mt-3 text-4xl font-bold md:mt-4 md:text-5xl lg:text-6xl text-gray-900">
                {title}
              </h1>
              <p className="mt-5 text-base md:mt-6 md:text-lg text-gray-600">
                {subtitle}
              </p>
            </div>
          </div>
        )}

        {/* Filtros mejorados */}
        <div className="mb-12 bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-6">Filtrar vehículos</h3>
          <div className="flex flex-col lg:flex-row lg:items-end gap-6 lg:gap-8">
            {/* Filtro de marca - solo mostrar si NO hay marca preseleccionada */}
            {!selectedBrandProp && (
              <div className="flex flex-col min-w-[200px] flex-1">
                <label className="block mb-2 font-medium text-gray-700">Marca</label>
                <div className="relative">
                  <select
                    className="w-full border border-gray-300 rounded-lg px-4 py-3 pr-10 appearance-none bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    value={selectedBrand}
                    onChange={e => setSelectedBrand(e.target.value)}
                  >
                    <option value="Todas">Todas las marcas</option>
                    {allBrands.map(brand => (
                      <option key={brand} value={brand}>{brand}</option>
                    ))}
                  </select>
                  <FaChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" />
                </div>
              </div>
            )}
            
            {/* Filtro de modelo - solo mostrar si HAY marca preseleccionada */}
            {selectedBrandProp && (
              <div className="flex flex-col min-w-[200px] flex-1">
                <label className="block mb-2 font-medium text-gray-700">Modelo</label>
                <div className="relative">
                  <select
                    className="w-full border border-gray-300 rounded-lg px-4 py-3 pr-10 appearance-none bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    onChange={e => {
                      if (e.target.value !== 'todos') {
                        const selectedProduct = allProducts.find(p => p.slug === e.target.value);
                        if (selectedProduct) {
                          window.location.href = `/vehiculos/${selectedProduct.brandSlug}/${selectedProduct.slug}`;
                        }
                      }
                    }}
                    defaultValue="todos"
                  >
                    <option value="todos">Todos los modelos</option>
                    {allProducts.map(product => (
                      <option key={product.slug} value={product.slug}>{product.name}</option>
                    ))}
                  </select>
                  <FaChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" />
                </div>
              </div>
            )}

            {/* Filtro de precio */}
            <div className="flex flex-col min-w-[280px] flex-1">
              <label className="block mb-2 font-medium text-gray-700">Rango de precio</label>
              <div className="bg-gray-50 rounded-lg p-4">
                <PriceRangeSlider
                  min={priceRange[0]}
                  max={priceRange[1]}
                  value={priceRange}
                  onChange={setPriceRange}
                  minGap={1000}
                  textColor="#374151"
                  subTextColor="#6B7280"
                />
              </div>
            </div>

            {/* Botón de reset/navegación */}
            <div className="flex flex-col justify-end">
              {selectedBrandProp ? (
                <Link
                  href="/vehiculos"
                  className="px-6 py-3 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium text-center"
                >
                  Ver todas las marcas
                </Link>
              ) : (
                <button
                  onClick={() => {
                    setSelectedBrand('Todas');
                    if (allProducts.length > 0) {
                      const prices = allProducts.map(p => p.priceValue);
                      setPriceRange([Math.min(...prices), Math.max(...prices)]);
                    }
                  }}
                  className="px-6 py-3 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                >
                  Limpiar filtros
                </button>
              )}
            </div>
          </div>

          {/* Contador de resultados */}
          <div className="mt-4 pt-4 border-t border-gray-200">
            <p className="text-sm text-gray-600">
              Mostrando <span className="font-semibold">{paginatedProducts.length}</span> de <span className="font-semibold">{filteredProducts.length}</span> vehículo{filteredProducts.length !== 1 ? 's' : ''}
              {selectedBrand !== 'Todas' && ` de ${selectedBrand}`}
            </p>
          </div>
        </div>

        {/* Grilla de productos */}
        {isLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[...Array(12)].map((_, i) => (
              <div key={i} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden animate-pulse">
                <div className="aspect-[4/3] bg-gray-200"></div>
                <div className="p-4 space-y-3">
                  <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                  <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                  <div className="h-6 bg-gray-200 rounded w-1/3"></div>
                  <div className="h-10 bg-gray-200 rounded"></div>
                </div>
              </div>
            ))}
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="max-w-md mx-auto">
              <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6-4h6m2 5.291A7.962 7.962 0 0112 15c-2.34 0-4.47-.881-6.08-2.33" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No hay vehículos disponibles</h3>
              <p className="text-gray-600 mb-4">
                {selectedBrandProp ? 
                  `No encontramos vehículos de ${selectedBrandProp} en este momento.` : 
                  'No encontramos vehículos que coincidan con los filtros seleccionados.'
                }
              </p>
              {selectedBrandProp ? (
                <Link 
                  href="/vehiculos"
                  className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Ver todos los vehículos
                </Link>
              ) : (
                <button
                  onClick={() => {
                    setSelectedBrand('Todas');
                    if (allProducts.length > 0) {
                      const prices = allProducts.map(p => p.priceValue);
                      setPriceRange([Math.min(...prices), Math.max(...prices)]);
                    }
                  }}
                  className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Limpiar filtros
                </button>
              )}
            </div>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {paginatedProducts.map(product => (
                <Link
                  key={product.id}
                  href={`/vehiculos/${product.brandSlug}/${product.slug}`}
                  className="group bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-lg transition-all duration-300 hover:-translate-y-1"
                >
                  <div className="relative aspect-[4/3] overflow-hidden">
                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      onError={(e) => {
                        e.target.src = "https://d22po4pjz3o32e.cloudfront.net/placeholder-image.svg";
                      }}
                      loading="lazy"
                    />
                    <div className="absolute top-3 left-3">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Nuevo
                      </span>
                    </div>
                  </div>
                  <div className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      {BRAND_LOGOS[product.brand] && (
                        <img 
                          src={BRAND_LOGOS[product.brand]} 
                          alt={product.brand}
                          className="w-5 h-5 object-contain"
                          onError={(e) => e.target.style.display = 'none'}
                        />
                      )}
                      <span className="text-sm text-gray-500 font-medium">{product.brand}</span>
                    </div>
                    <h3 className="font-semibold text-gray-900 text-lg mb-1 group-hover:text-blue-600 transition-colors">
                      {product.name}
                    </h3>
                    <p className="text-sm text-gray-600 mb-2">{product.categoria}</p>
                    <p className="text-xl font-bold text-gray-900 mb-3">{product.price}</p>
                    <button className="w-full px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors font-medium">
                      Ver detalles
                    </button>
                  </div>
                </Link>
              ))}
            </div>

            {/* Paginación */}
            {totalPages > 1 && (
              <div className="mt-12 flex justify-center">
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Anterior
                  </button>
                  
                  {[...Array(totalPages)].map((_, i) => {
                    const pageNumber = i + 1;
                    const isActive = pageNumber === currentPage;
                    
                    return (
                      <button
                        key={pageNumber}
                        onClick={() => setCurrentPage(pageNumber)}
                        className={`px-4 py-2 rounded-lg transition-colors ${
                          isActive 
                            ? 'bg-gray-900 text-white' 
                            : 'border border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {pageNumber}
                      </button>
                    );
                  })}
                  
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Siguiente
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
} 